import { Slider } from '../../../Slider';
import {
  calculateFairValueBreakdown,
  getCompanyFinancialData,
} from '../../../../utils/calculation';

import type { NarrativeValuationCompany } from '../../../../hooks/useNarrativeValuationCompany';
import { tooltipContent } from '../../tooltipContent';
import { INPUT_VALUE_RANGES } from '../../../../utils/validation';
import { LabelledValue } from '../LabelledValue';
import { VALUATOR_RATE_PRECISION } from '@/pages/Narrative/utils/schema';
import { NarrativeValuatorTooltip } from '../NarrativeValuatorTooltip';
import { OperatorSymbol } from './components/OperatorSymbol';
import { NarrativeValuatorForm } from '../../utils';
import { format, isValid, parseISO } from 'date-fns';
import { getTradingItemCurrencyISO } from '@/utilities/getTradingItemCurrencyISO';
import { PastAnnualRevenueEarnings } from '@/pages/Narrative/types';

export type Props = {
  company: NarrativeValuationCompany;
  onChange: (data: NarrativeValuatorForm) => void;
  values: NarrativeValuatorForm;
  pastAnnualRevenueEarnings?: PastAnnualRevenueEarnings;
};

export const CalculationBreakdown = ({
  company,
  onChange,
  values,
  pastAnnualRevenueEarnings,
}: Props) => {
  const { discountRatePercentage, priceEarningsMultiple } = values;
  const companyFinancialData = getCompanyFinancialData(company);

  const mostRecentReportedYear =
    pastAnnualRevenueEarnings &&
    pastAnnualRevenueEarnings.mostRecentReportedDate &&
    isValid(parseISO(pastAnnualRevenueEarnings.mostRecentReportedDate))
      ? format(
          parseISO(pastAnnualRevenueEarnings.mostRecentReportedDate),
          'yyyy'
        )
      : new Date().getFullYear().toString();

  const { presentRevenue: revenue } = companyFinancialData; // is required for create a narrative before save

  const isMostRecentReportedYearInYearlyTimeSeries =
    pastAnnualRevenueEarnings &&
    pastAnnualRevenueEarnings.yearlyTimeSeries &&
    mostRecentReportedYear in pastAnnualRevenueEarnings.yearlyTimeSeries;

  const presentRevenue = isMostRecentReportedYearInYearlyTimeSeries
    ? Number(
        pastAnnualRevenueEarnings.yearlyTimeSeries[mostRecentReportedYear]
          .revenue
      )
    : revenue; // the yearlyTimeSeries revenue does not require multiplying by reporting unit

  const breakdown = calculateFairValueBreakdown({
    currencyISO: getTradingItemCurrencyISO(company.data.currencyInfo) ?? 'XXX',
    currentYear: Number(mostRecentReportedYear),
    input: values,
    presentRevenue,
  });

  const { formatted } = breakdown;

  const {
    fairValue,
    futureMarketCap,
    futureEarnings,
    futureYear,
    currentYear,
    sharePriceFutureYear,
    futureSharesOutstanding,
  } = formatted;

  const handleOnChange = (type: string) => (value: number) => {
    onChange({
      ...values,
      [type]: value,
    });
  };

  return (
    <div className="grid gap-x2">
      {/* Risk level section */}
      <div className="grid gap-x1">
        <div className="p-x2">
          <div className="grid items-center grid-cols-[auto_min-content]">
            <div className="text-xs font-strong text-solid">
              Risk Level (Discount Rate)
            </div>
            <NarrativeValuatorTooltip>
              {tooltipContent.discountRate}
            </NarrativeValuatorTooltip>
          </div>
          <div className="text-xs text-soft">
            Our default considers factors like the company&apos;s size,
            volatility, profitablity and country of operation.
          </div>
        </div>
        <div className="p-x2">
          <div className="mb-x4">
            <Slider
              identifier="narrative-valuator-discount-rate-percentage"
              minRange={INPUT_VALUE_RANGES.discountRatePercentage.min}
              maxRange={INPUT_VALUE_RANGES.discountRatePercentage.max}
              value={discountRatePercentage}
              precision={VALUATOR_RATE_PRECISION}
              onChange={handleOnChange('discountRatePercentage')}
              suffix="%"
              bands={[
                {
                  threshold: INPUT_VALUE_RANGES.discountRatePercentage.min,
                  label: 'Decrease',
                  align: 'left',
                },
                {
                  threshold: INPUT_VALUE_RANGES.discountRatePercentage.max,
                  label: 'Increase',
                  align: 'right',
                },
              ]}
            />
          </div>
          <LabelledValue label="Current discount rate">
            {companyFinancialData.discountRatePercentage.toFixed(2)}%
          </LabelledValue>
        </div>
      </div>

      {/* Calculation section */}
      <div className="grid gap-x2">
        <div className="grid grid-cols-[auto_min-content] items-center px-x2">
          <div className="text-xs font-strong text-solid">Calculation</div>
          <NarrativeValuatorTooltip>
            {tooltipContent.calculationBreakdown}
          </NarrativeValuatorTooltip>
        </div>

        {/* First tile */}
        {/* Earnings 2029 X PE Ratio 2029 = Market Cap 2029 */}
        <div className="grid grid-cols-[auto_1fr_auto_1fr_auto] bg-p-grey-30 p-x2 gap-x1 text-tiny">
          <div>
            <div className="text-chart-02">{futureEarnings}</div>
            <div className="text-solid">Earnings {futureYear}</div>
          </div>

          <OperatorSymbol>x</OperatorSymbol>

          <div>
            <div className="text-chart-03">
              {priceEarningsMultiple.toFixed(2)}x
            </div>
            <div className="text-solid">PE Ratio {futureYear}</div>
          </div>

          <OperatorSymbol>=</OperatorSymbol>

          <div>
            <div className="text-chart-04">{futureMarketCap}</div>
            <div className="text-solid">Market Cap {futureYear}</div>
          </div>
        </div>

        {/* Second tile */}
        {/* Market Cap 2029 / Number of Shares Outstanding 2029 = Share Price 2029 */}
        <div className="grid grid-cols-[auto_1fr_auto_1fr_auto] bg-p-grey-30 p-x2 gap-x1 text-tiny">
          <div>
            <div className="text-chart-04">{futureMarketCap}</div>
            <div className="text-solid">Market Cap {futureYear}</div>
          </div>

          <OperatorSymbol>/</OperatorSymbol>

          <div>
            <div className="text-softer">{futureSharesOutstanding}</div>
            <div className="text-solid">No. shares {futureYear}</div>
          </div>

          <OperatorSymbol>=</OperatorSymbol>

          <div>
            <div className="text-chart-04">{sharePriceFutureYear}</div>
            <div className="text-solid">Share Price {futureYear}</div>
          </div>
        </div>

        {/* Third tile */}
        {/* 2029 Share Price (Discounted Back @ DR) = Fair Value Estimate 2024 */}
        <div className="grid grid-cols-[auto_1fr_auto] p-x2 rounded-b-x1 bg-p-grey-30 text-tiny">
          <div>
            <div>
              <span className="text-chart-04">{sharePriceFutureYear}</span>

              <span className="text-solid"> Share Price {futureYear}</span>
            </div>
            <div className="text-softer">
              Discounted to {mostRecentReportedYear} @{' '}
              {discountRatePercentage.toFixed(2)}% p.a.
            </div>
          </div>

          <OperatorSymbol>=</OperatorSymbol>

          <div>
            <div className="text-chart-01">{fairValue}</div>
            <div className="text-solid">Fair Value {currentYear}</div>
          </div>
        </div>
      </div>
    </div>
  );
};
